// @flow
import { findLast, flatten, flattenDeep } from 'lodash';
import URI from 'urijs';
import {
  DEPARTURE_CITY_FACET_ID,
  DEPARTURE_DATE_MAX_FACET_ID,
  DEPARTURE_DATE_MIN_FACET_ID,
  DURATION_FACET,
  LOCALE_FACET_ID,
  OFFER_ID_FACET_ID,
  PRODUCT_FACET_ID,
} from 'Utils/faceting/constants';

const FILTER_PARAM = 'filters';

export type Filter = {
  name: string,
  value: string | number,
  operation?: string,
};

export function composeFiltersAsPayload(...filters: Filter[][]): string {
  const formData: string[] = flattenDeep(filters).reduce((acc, { name, value, operation }) => {
    if (value) acc.push(`${FILTER_PARAM}[${name}][values][]=${URI.encode(value)}`);
    if (value && operation) {
      acc.push(`${FILTER_PARAM}[${name}][operations][]=${URI.encode(operation)}`);
    }
    return acc;
  }, []);
  return formData.join('&');
}

function buildFilters({ name, value, operation }): Filter[] {
  return value ? [{ name, value, operation }] : [];
}

export function getLocaleFilters(locale: string, operation?: string = 'and'): Filter[] {
  return buildFilters({ name: LOCALE_FACET_ID, value: locale, operation });
}
export function getDepartureDateFilters(
  departureDate?: string,
  operation?: string = 'and',
): Filter[] {
  return [
    ...buildFilters({ name: DEPARTURE_DATE_MIN_FACET_ID, value: departureDate, operation }),
    ...buildFilters({ name: DEPARTURE_DATE_MAX_FACET_ID, value: departureDate, operation }),
  ];
}
export function getDepartureCityFilters({
  facets = [],
  defaultValue = '',
  operation = 'and',
}: {
  facets?: ActiveFacet[],
  defaultValue?: string,
  operation?: string,
}): Filter[] {
  const departureCityFacet = findLast(facets, ({ id }) => id === DEPARTURE_CITY_FACET_ID) || {};
  const value = departureCityFacet.name || defaultValue;
  return buildFilters({ name: DEPARTURE_CITY_FACET_ID, value, operation });
}
export function getDurationFilters(duration?: number, operation?: string = 'and') {
  return buildFilters({ name: DURATION_FACET, value: duration, operation });
}

function getProductIdFilters(id: string, operation: string = 'or'): Filter[] {
  return buildFilters({ name: PRODUCT_FACET_ID, value: id, operation });
}
export function getProductIdsFilters(ids?: string[] = [], operation?: string): Filter[] {
  return flatten(ids.map(id => getProductIdFilters(id, operation)));
}

export function getOfferFilters(offerId?: string, operation?: string = 'and') {
  return buildFilters({ name: OFFER_ID_FACET_ID, value: offerId, operation });
}

const FACETS_TO_OMIT = [
  DEPARTURE_DATE_MIN_FACET_ID,
  DEPARTURE_DATE_MAX_FACET_ID,
  DEPARTURE_CITY_FACET_ID,
  DURATION_FACET,
];
export function getFacetsFilters(facets?: ActiveFacet[] = [], operation: string = 'and'): Filter[] {
  const filters: Filter[][] = facets
    .filter(facet => !FACETS_TO_OMIT.includes(facet.id))
    .map(facet => buildFilters({ name: facet.id, value: facet.name, operation }));
  return flatten(filters);
}
